/* Organizer Website — mandatory footer: navigation, legal, contact, social,
   copyright. White-label: the ONLY platform reference is the single
   "Powered by …" line. */
import { Facebook, Instagram, Linkedin, Mail, MapPin, Music2, Phone, Twitter, Youtube } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { BRAND } from "@/config/brand";
import { mediaUrlFromMap } from "@/lib/api";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, x: Twitter, linkedin: Linkedin, tiktok: Music2 };

export const WebsiteFooter = ({ organizer, website, media, eventId = null }) => {
  const { t } = useTranslation();
  const base = `/${organizer.slug}`;
  const contact = website?.contact || {};
  const email = contact.email || organizer.contact_email;
  const socials = Object.entries(contact.socials || {}).filter(([, url]) => url);
  const logoUrl = mediaUrlFromMap(media, organizer.theme?.logo_media_id);
  const hasAbout = Boolean(
    (website?.modules || []).some((m) => m.type === "about" && m.enabled) ||
      Object.keys(organizer.description || {}).length
  );

  const navLinks = [
    { to: base, label: t("web.home"), testid: "website-footer-home" },
    { to: `${base}#auctions`, label: t("web.auctions"), testid: "website-footer-auctions" },
    ...(hasAbout ? [{ to: `${base}/about`, label: t("web.about"), testid: "website-footer-about" }] : []),
    { to: `${base}#how-to-bid`, label: t("web.howToBid"), testid: "website-footer-htb" },
  ];
  const legalLinks = [
    ...(eventId ? [{ to: `/auction-terms/${eventId}`, label: t("legal.auctionTerms"), testid: "website-footer-auction-terms" }] : []),
    { to: "/legal/terms", label: t("legal.platformTerms"), testid: "website-footer-terms" },
    { to: "/legal/privacy", label: t("footer.privacy"), testid: "website-footer-privacy" },
    { to: "/legal/cookies", label: t("footer.cookies"), testid: "website-footer-cookies" },
  ];

  const colTitle = "text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40";
  const link = "text-[13px] text-white/60 hover:text-white transition-colors duration-200 w-fit";

  return (
    <footer id="contact" className="scroll-mt-20 bg-[#0A0A0A]" data-testid="website-footer">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
        <div className="col-span-2 lg:col-span-1 flex flex-col gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt={organizer.name} loading="lazy" className="h-9 w-auto max-w-[160px] object-contain brightness-0 invert opacity-90" />
          ) : (
            <span className="text-sm font-semibold tracking-[0.12em] uppercase text-white brand-heading">{organizer.name}</span>
          )}
          {organizer.legal?.legal_name && <p className="text-xs text-white/40">{organizer.legal.legal_name}</p>}
          {socials.length > 0 && (
            <div className="flex items-center gap-2 pt-1" data-testid="website-footer-socials">
              {socials.map(([key, url]) => {
                const Icon = SOCIAL_ICONS[key];
                return Icon ? (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key}
                    data-testid={`website-footer-social-${key}`}
                    className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-colors duration-200"
                  >
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </a>
                ) : null;
              })}
            </div>
          )}
        </div>

        <nav className="flex flex-col gap-2.5" aria-label={t("web.navigation")}>
          <span className={colTitle}>{t("web.navigation")}</span>
          {navLinks.map((l) => (
            <Link key={l.testid} to={l.to} data-testid={l.testid} className={link}>
              {l.label}
            </Link>
          ))}
        </nav>

        <nav className="flex flex-col gap-2.5" aria-label={t("legal.footerNav")}>
          <span className={colTitle}>{t("legal.footerNav")}</span>
          {legalLinks.map((l) => (
            <Link key={l.testid} to={l.to} data-testid={l.testid} className={link}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex flex-col gap-2.5" data-testid="website-footer-contact">
          <span className={colTitle}>{t("web.contact")}</span>
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className={`${link} inline-flex items-center gap-2`}>
              <Phone className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {contact.phone}
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className={`${link} inline-flex items-center gap-2`}>
              <Mail className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {email}
            </a>
          )}
          {contact.address && (
            <span className="text-[13px] text-white/60 inline-flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              {contact.address}
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-xs text-white/35">
            © {new Date().getFullYear()} {organizer.name}. {t("web.rightsReserved")}
          </p>
          <p className="text-xs text-white/35" data-testid="website-footer-powered-by">
            {t("footer.poweredBy", { name: BRAND.poweredBy })}
          </p>
        </div>
      </div>
    </footer>
  );
};
